import { AlertTriangle, CheckCircle2, ChevronDown, Play, RefreshCw, Trash2, X } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "../api";
import type { EngineInputField, EngineStep, OperationName, ShowEngine, ShowOperation, ShowPage, ShowStory } from "../show-generator";
import { activeOperation } from "../show-generator";
import type { Session } from "../types";
import { StatusBadge } from "./StatusBadge";

interface SelectedCommand {
  step: EngineStep;
  name: OperationName;
}

export function ShowOperationLauncher({
  story,
  engine,
  session,
  onOperationFinished,
  onActiveChange,
  renderHeader,
}: {
  story: ShowStory;
  engine: ShowEngine;
  session: Session;
  onOperationFinished?: () => void;
  onActiveChange?: (active: boolean) => void;
  renderHeader: (controls: ReactNode) => ReactNode;
}) {
  const [menuOpen, setMenuOpen] = useState<OperationName | null>(null);
  const [selected, setSelected] = useState<SelectedCommand | null>(null);
  const [tracked, setTracked] = useState<ShowOperation | null>(null);
  const [activeOnServer, setActiveOnServer] = useState(false);
  const [activeLoading, setActiveLoading] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);

  const loadActiveOperations = useCallback(async () => {
    setActiveLoading(true);
    try {
      const result = await apiRequest<ShowPage<ShowOperation>>(`/api/show-generator/operations?storyId=${encodeURIComponent(story.id)}&active=true&page=1&pageSize=10`);
      const active = result.data.some((operation) => activeOperation(operation.status));
      setActiveOnServer(active);
      onActiveChange?.(active);
    } catch {
      setActiveOnServer(true);
      onActiveChange?.(true);
    } finally {
      setActiveLoading(false);
    }
  }, [onActiveChange, story.id]);

  useEffect(() => { void loadActiveOperations(); }, [loadActiveOperations]);

  useEffect(() => {
    if (menuOpen === null) return;
    function closeMenu(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(null);
    }
    document.addEventListener("mousedown", closeMenu);
    return () => document.removeEventListener("mousedown", closeMenu);
  }, [menuOpen]);

  useEffect(() => {
    if (!tracked || !activeOperation(tracked.status)) return;
    const timer = window.setInterval(async () => {
      try {
        const operation = await apiRequest<ShowOperation>(`/api/show-generator/operations/${tracked.id}`);
        setTracked(operation);
        if (!activeOperation(operation.status)) {
          void loadActiveOperations();
          onOperationFinished?.();
        }
      } catch { /* Keep the latest visible operation state when polling is interrupted. */ }
    }, 3000);
    return () => window.clearInterval(timer);
  }, [loadActiveOperations, onOperationFinished, tracked]);

  function selectCommand(step: EngineStep, name: OperationName) {
    setSelected({ step, name });
    setMenuOpen(null);
  }

  const deleteBlocked = activeLoading || activeOnServer || Boolean(tracked && activeOperation(tracked.status));
  const controls = <div ref={menuRef} className="story-command-controls">
    {engine.steps.some((step) => step.operations.includes("generate")) ? <CommandMenuButton name="generate" label="Operation" open={menuOpen === "generate"} disabled={false} engine={engine} onToggle={() => setMenuOpen((current) => current === "generate" ? null : "generate")} onSelect={selectCommand} /> : null}
    {engine.steps.some((step) => step.operations.includes("delete")) ? <CommandMenuButton name="delete" label="Delete" open={menuOpen === "delete"} disabled={deleteBlocked} engine={engine} onToggle={() => setMenuOpen((current) => current === "delete" ? null : "delete")} onSelect={selectCommand} /> : null}
  </div>;

  return <>
    {renderHeader(controls)}
    {selected || tracked ? <section className="story-operation-launcher" aria-label="Story step operations">
    {selected ? <div className="story-operation-form-panel">
      <header><div><p className="eyebrow">{selected.step.id}</p><h2>{selected.name} {stepName(selected.step.id)}</h2></div><button className="icon-button" type="button" title="Close operation form" onClick={() => setSelected(null)}><X size={17} /></button></header>
      <OperationForm key={`${selected.step.id}:${selected.name}`} story={story} step={selected.step} name={selected.name} session={session} onCreated={(operation) => { setTracked(operation); setActiveOnServer(true); onActiveChange?.(true); }} />
    </div> : null}
    {tracked ? <section className="tracked-operation">
      <div><span>Latest submitted operation</span><strong>{tracked.name} · {tracked.step}</strong><small>{tracked.stage ?? "Pending stage"}</small></div>
      <StatusBadge value={tracked.status} />
      {activeOperation(tracked.status) ? <RefreshCw className="spin" size={17} /> : tracked.status === "succeeded" ? <CheckCircle2 size={17} /> : <AlertTriangle size={17} />}
    </section> : null}
    </section> : null}
  </>;
}

function CommandMenuButton({ name, label, open, disabled, engine, onToggle, onSelect }: { name: OperationName; label: string; open: boolean; disabled: boolean; engine: ShowEngine; onToggle: () => void; onSelect: (step: EngineStep, name: OperationName) => void }) {
  const destructive = name === "delete";
  return <div className="step-operation-menu">
    <button
      className={destructive ? "danger-action story-command-button" : "secondary-button story-command-button"}
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      disabled={disabled}
      title={destructive && disabled ? "Delete is unavailable while an operation is active" : `${label} step target`}
      onClick={onToggle}
    >
      {destructive ? <Trash2 size={15} /> : <Play size={15} />}{label}<ChevronDown size={14} />
    </button>
    {open ? <div className={`step-operation-popover${destructive ? " delete-popover" : ""}`} role="menu">
      {engine.steps.filter((step) => step.operations.includes(name)).map((step, index) => <section key={step.id} aria-label={`${name}-${step.id}`}>
        <button type="button" role="menuitem" onClick={() => onSelect(step, name)}>
          <span>{index}</span><div><strong>{stepName(step.id)}</strong><small>{step.description}</small></div>
        </button>
      </section>)}
    </div> : null}
  </div>;
}

function OperationForm({ story, step, name, session, onCreated }: { story: ShowStory; step: EngineStep; name: OperationName; session: Session; onCreated: (operation: ShowOperation) => void }) {
  const fields = step.inputFields[name] ?? [];
  const defaults = useMemo(() => initialValues(fields), [fields]);
  const [values, setValues] = useState<Record<string, string | number>>(defaults);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function submit() {
    setSubmitting(true);
    setError("");
    try {
      const input: Record<string, string | number> = {};
      for (const field of fields) {
        if (!fieldVisible(field, values)) continue;
        const value = values[field.name];
        if (field.control === "integer") input[field.name] = Number(value);
        else if (value !== "" && value !== undefined) input[field.name] = value;
      }
      const operation = await apiRequest<ShowOperation>("/api/show-generator/operations", {
        method: "POST",
        body: JSON.stringify({ storyId: story.id, engine: story.engine, step: step.id, name, input }),
      }, session.csrfToken);
      setConfirmDelete(false);
      onCreated(operation);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Could not submit operation");
    } finally { setSubmitting(false); }
  }

  function requestSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (name === "delete") setConfirmDelete(true);
    else void submit();
  }

  return <>
    <form className="operation-form" onSubmit={requestSubmit}>
      {fields.filter((field) => fieldVisible(field, values)).map((field) => <OperationField key={field.name} field={field} value={values[field.name] ?? ""} onChange={(value) => setValues((current) => ({ ...current, [field.name]: value }))} />)}
      <button className={name === "delete" ? "danger-action" : "primary-button"} type="submit" disabled={submitting}>{name === "delete" ? <Trash2 size={15} /> : null}{submitting ? "Submitting…" : name === "delete" ? "Delete target" : "Generate"}</button>
    </form>
    {error ? <div className="inline-error" role="alert">{error}</div> : null}
    {confirmDelete ? <div className="modal-backdrop"><section className="confirm-modal" role="dialog" aria-modal="true" aria-labelledby={`delete-${step.id}`}>
      <header><div><p className="eyebrow">Destructive operation</p><h2 id={`delete-${step.id}`}>Delete {stepName(step.id)} target</h2></div><button className="icon-button" type="button" onClick={() => setConfirmDelete(false)} title="Cancel delete"><X size={18} /></button></header>
      <dl><div><dt>Story</dt><dd>{story.id}</dd></div><div><dt>Step</dt><dd>{step.id}</dd></div>{Object.entries(values).map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{String(value)}</dd></div>)}</dl>
      <footer><button className="secondary-button" type="button" onClick={() => setConfirmDelete(false)}>Cancel</button><button className="danger-button" type="button" onClick={() => void submit()} disabled={submitting}><Trash2 size={15} />{submitting ? "Deleting…" : "Confirm delete"}</button></footer>
    </section></div> : null}
  </>;
}

function OperationField({ field, value, onChange }: { field: EngineInputField; value: string | number; onChange: (value: string | number) => void }) {
  const common = { required: field.required, "aria-label": field.label };
  return <label className={field.control === "textarea" ? "operation-field-wide" : ""}><span>{field.label}</span>
    {field.control === "select" ? <select {...common} value={value} onChange={(event) => onChange(event.target.value)}>{field.options?.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select>
      : field.control === "textarea" ? <textarea {...common} value={value} maxLength={field.max} rows={4} onChange={(event) => onChange(event.target.value)} />
        : <input {...common} value={value} type={field.control === "integer" ? "number" : "text"} min={field.min} max={field.max} maxLength={field.control === "text" ? field.max : undefined} onChange={(event) => onChange(field.control === "integer" ? Number(event.target.value) : event.target.value)} />}
    {field.description ? <small>{field.description}</small> : null}
  </label>;
}

function initialValues(fields: EngineInputField[]): Record<string, string | number> {
  return Object.fromEntries(fields.map((field) => [field.name, field.defaultValue ?? (field.control === "integer" ? field.min ?? 1 : field.options?.[0]?.value ?? "")]));
}

function fieldVisible(field: EngineInputField, values: Record<string, string | number>): boolean {
  return !field.visibleWhen || values[field.visibleWhen.field] === field.visibleWhen.equals;
}

function stepName(stepId: string): string {
  return stepId.replace(/^\d+-/, "").replaceAll("-", " ");
}
